import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import BlogEditor from '../components/BlogEditor';
import NewsletterBuilder from '../components/NewsletterBuilder';
import ApplicationViewer from '../components/ApplicationViewer';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  DollarSign, Users, TrendingUp, Calendar, FileText, UserCheck, 
  GraduationCap, Briefcase, ChevronDown, ChevronUp, Mail, Phone, 
  MapPin, PenSquare, Send, Plus, Edit, Trash2, Eye, Image, 
  Contact, Search, Download, Filter, Shield, LogOut, Key, UserCog,
  MessageCircle, Reply, Clock, CheckCircle, Circle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user, logout, hasPermission, getAuthHeader } = useAuth();
  const [activeTab, setActiveTab] = useState('donations');
  
  // Admin Users state
  const [adminUsers, setAdminUsers] = useState([]);
  const [showUserForm, setShowUserForm] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userForm, setUserForm] = useState({
    email: '', password: '', name: '', role: 'manager', permissions: {}
  });
  const [permissionsSchema, setPermissionsSchema] = useState(null);

  // Contact submissions state (Inbox)
  const [contactSubmissions, setContactSubmissions] = useState([]);
  const [inboxStats, setInboxStats] = useState({ total: 0, new: 0, read: 0, replied: 0 });
  const [selectedContact, setSelectedContact] = useState(null);
  const [replyMessage, setReplyMessage] = useState('');
  const [showReplyModal, setShowReplyModal] = useState(false);
  
  // Donations state
  const [donations, setDonations] = useState([]);
  const [donationStats, setDonationStats] = useState({
    totalDonations: 0,
    totalAmount: 0,
    monthlyDonations: 0,
    oneTimeDonations: 0
  });
  
  // Applications state
  const [cubApplications, setCubApplications] = useState([]);
  const [lionApplications, setLionApplications] = useState([]);
  const [appStats, setAppStats] = useState({
    cubApplications: { total: 0, pending: 0, accepted: 0 },
    lionApplications: { total: 0, pending: 0, accepted: 0 }
  });
  const [expandedApp, setExpandedApp] = useState(null);
  const [viewingApplication, setViewingApplication] = useState(null);
  const [viewingApplicationType, setViewingApplicationType] = useState(null);
  
  // Blog state
  const [blogPosts, setBlogPosts] = useState([]);
  const [showBlogEditor, setShowBlogEditor] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featureImage: '',
    author: '',
    tags: '',
    metaTitle: '',
    metaDescription: '',
    status: 'draft',
    scheduledAt: ''
  });
  
  // Newsletter state
  const [newsletters, setNewsletters] = useState([]);
  const [showNewsletterEditor, setShowNewsletterEditor] = useState(false);
  const [editingNewsletter, setEditingNewsletter] = useState(null);
  const [newsletterForm, setNewsletterForm] = useState({
    subject: '',
    content: '',
    template: 'general',
    recipientType: 'all'
  });
  const [subscriberStats, setSubscriberStats] = useState({
    total: 0,
    active: 0,
    bySource: { website: 0, donation: 0, application: 0 },
    totalReach: { subscribers: 0, donors: 0, applicants: 0 }
  });
  
  // Contacts state
  const [contacts, setContacts] = useState([]);
  const [contactStats, setContactStats] = useState({
    total: 0,
    unique_emails: 0,
    by_type: { subscribers: 0, donors: 0, cubs: 0, lions: 0, mentors: 0, leaders: 0 }
  });
  const [contactFilter, setContactFilter] = useState('all');
  const [contactSearch, setContactSearch] = useState('');
  const [showAddContact, setShowAddContact] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    contactType: 'subscriber',
    company: '',
    notes: ''
  });
  
  const [loading, setLoading] = useState(true);

  // Fetch functions
  const fetchAdminUsers = useCallback(async () => {
    if (!hasPermission('users', 'view')) return;
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users`, { headers: getAuthHeader() });
      setAdminUsers(response.data);
    } catch (error) {
      console.error('Error fetching admin users:', error);
    }
  }, [hasPermission, getAuthHeader]);

  const fetchPermissionsSchema = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/permissions`, { headers: getAuthHeader() });
      setPermissionsSchema(response.data);
    } catch (error) {
      console.error('Error fetching permissions schema:', error);
    }
  }, [getAuthHeader]);

  const fetchContactSubmissions = useCallback(async () => {
    try {
      const [submissionsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/contact`, { headers: getAuthHeader() }),
        axios.get(`${BACKEND_URL}/api/admin/contact/stats`, { headers: getAuthHeader() })
      ]);
      setContactSubmissions(submissionsRes.data);
      setInboxStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching contact submissions:', error);
    }
  }, [getAuthHeader]);

  const fetchDonations = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/donations`);
      setDonations(response.data);
    } catch (error) {
      console.error('Error fetching donations:', error);
    }
  }, []);

  const fetchDonationStats = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/donations/stats`);
      setDonationStats(response.data);
    } catch (error) {
      console.error('Error fetching donation stats:', error);
    }
  }, []);

  const fetchApplications = useCallback(async () => {
    try {
      const [cubRes, lionRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/applications/cub`),
        axios.get(`${BACKEND_URL}/api/admin/applications/lion`),
        axios.get(`${BACKEND_URL}/api/admin/applications/stats`)
      ]);
      setCubApplications(cubRes.data);
      setLionApplications(lionRes.data);
      setAppStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching applications:', error);
    }
  }, []);

  const fetchBlogPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/blog`);
      setBlogPosts(response.data);
    } catch (error) {
      console.error('Error fetching blog posts:', error);
    }
  }, []);

  const fetchNewsletters = useCallback(async () => {
    try {
      const [newsletterRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/newsletter`),
        axios.get(`${BACKEND_URL}/api/admin/subscribers/stats`)
      ]);
      setNewsletters(newsletterRes.data);
      setSubscriberStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching newsletters:', error);
    }
  }, []);

  const fetchContacts = useCallback(async (filterType = 'all') => {
    try {
      const [contactsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/admin/contacts?contact_type=${filterType}`),
        axios.get(`${BACKEND_URL}/api/admin/contacts/stats`)
      ]);
      setContacts(contactsRes.data);
      setContactStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([
        fetchDonations(),
        fetchDonationStats(),
        fetchApplications(),
        fetchBlogPosts(),
        fetchNewsletters(),
        fetchContacts(),
        fetchAdminUsers(),
        fetchPermissionsSchema(),
        fetchContactSubmissions()
      ]);
      setLoading(false);
    };
    loadData();
  }, [fetchDonations, fetchDonationStats, fetchApplications, fetchBlogPosts, fetchNewsletters, fetchContacts, fetchAdminUsers, fetchPermissionsSchema, fetchContactSubmissions]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const statusStyles = {
      pending: 'bg-yellow-100 text-yellow-800',
      reviewed: 'bg-blue-100 text-blue-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      sent: 'bg-blue-100 text-blue-800'
    };
    return statusStyles[status] || 'bg-gray-100 text-gray-800';
  };

  // Blog functions
  const generateSlug = (title) => {
    return title.toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleBlogTitleChange = (e) => {
    const title = e.target.value;
    setBlogForm(prev => ({
      ...prev,
      title,
      slug: editingPost ? prev.slug : generateSlug(title)
    }));
  };

  // User management handlers
  const handleLogout = () => {
    logout();
    navigate('/admin');
  };

  const handleSaveUser = async () => {
    if (!userForm.email || !userForm.name) {
      toast.error('Please fill in email and name');
      return;
    }
    if (!editingUser && !userForm.password) {
      toast.error('Password is required for new users');
      return;
    }

    try {
      if (editingUser) {
        await axios.put(`${BACKEND_URL}/api/admin/users/${editingUser.id}`, {
          name: userForm.name,
          role: userForm.role,
          permissions: userForm.permissions,
          isActive: userForm.isActive
        }, { headers: getAuthHeader() });
        toast.success('User updated successfully');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/users`, userForm, { headers: getAuthHeader() });
        toast.success('User created successfully');
      }
      setShowUserForm(false);
      setEditingUser(null);
      setUserForm({ email: '', password: '', name: '', role: 'manager', permissions: {} });
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving user');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/users/${userId}`, { headers: getAuthHeader() });
      toast.success('User deleted successfully');
      fetchAdminUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error deleting user');
    }
  };

  const handleResetPassword = async (userId) => {
    if (!window.confirm('This will reset the user password to a temporary one. Continue?')) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/users/${userId}/reset-password`, {}, { headers: getAuthHeader() });
      toast.success(`Password reset. Temporary password: ${response.data.temporaryPassword}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error resetting password');
    }
  };

  const openEditUser = (adminUser) => {
    setEditingUser(adminUser);
    setUserForm({
      email: adminUser.email,
      password: '',
      name: adminUser.name,
      role: adminUser.role,
      permissions: adminUser.permissions || {},
      isActive: adminUser.isActive
    });
    setShowUserForm(true);
  };

  const openNewUser = () => {
    setEditingUser(null);
    const defaultPerms = permissionsSchema?.defaultPermissions?.manager || {};
    setUserForm({ email: '', password: '', name: '', role: 'manager', permissions: defaultPerms });
    setShowUserForm(true);
  };

  // Contact handlers
  const handleMarkAsRead = async (submissionId) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/contact/${submissionId}/read`, {}, { headers: getAuthHeader() });
      fetchContactSubmissions();
    } catch (error) {
      toast.error('Failed to mark as read');
    }
  };

  const handleOpenReply = (submission) => {
    setSelectedContact(submission);
    setReplyMessage('');
    setShowReplyModal(true);
    if (submission.status === 'new') {
      handleMarkAsRead(submission.id);
    }
  };

  const handleSendReply = async () => {
    if (!replyMessage.trim()) {
      toast.error('Please enter a reply message');
      return;
    }

    try {
      await axios.post(`${BACKEND_URL}/api/admin/contact/${selectedContact.id}/reply`, 
        { message: replyMessage },
        { headers: getAuthHeader() }
      );
      toast.success('Reply sent successfully');
      setShowReplyModal(false);
      setSelectedContact(null);
      setReplyMessage('');
      fetchContactSubmissions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reply');
    }
  };

  const handleDeleteContact = async (submissionId) => {
    if (!window.confirm('Are you sure you want to delete this submission?')) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/admin/contact/${submissionId}`, { headers: getAuthHeader() });
      toast.success('Submission deleted');
      fetchContactSubmissions();
    } catch (error) {
      toast.error('Failed to delete submission');
    }
  };

  const handleRoleChange = (role) => {
    const defaultPerms = permissionsSchema?.defaultPermissions?.[role] || {};
    setUserForm(prev => ({ ...prev, role, permissions: defaultPerms }));
  };

  const togglePermission = (module, action) => {
    setUserForm(prev => {
      const currentPerms = prev.permissions[module] || [];
      const newPerms = currentPerms.includes(action)
        ? currentPerms.filter(a => a !== action)
        : [...currentPerms, action];
      return {
        ...prev,
        permissions: { ...prev.permissions, [module]: newPerms }
      };
    });
  };

  const saveBlogPost = async () => {
    if (!blogForm.title || !blogForm.content || !blogForm.author) {
      toast.error('Please fill in title, content, and author');
      return;
    }

    try {
      const postData = {
        ...blogForm,
        tags: blogForm.tags ? blogForm.tags.split(',').map(t => t.trim()) : []
      };

      if (editingPost) {
        await axios.put(`${BACKEND_URL}/api/admin/blog/${editingPost.id}`, postData);
        toast.success('Blog post updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/blog`, postData);
        toast.success('Blog post created');
      }
      
      setShowBlogEditor(false);
      setEditingPost(null);
      setBlogForm({
        title: '', slug: '', content: '', excerpt: '', featureImage: '',
        author: '', tags: '', metaTitle: '', metaDescription: '', status: 'draft', scheduledAt: ''
      });
      fetchBlogPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving blog post');
    }
  };

  const editBlogPost = (post) => {
    setBlogForm({
      ...post,
      tags: post.tags?.join(', ') || ''
    });
    setEditingPost(post);
    setShowBlogEditor(true);
  };

  const deleteBlogPost = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/blog/${postId}`);
      toast.success('Blog post deleted');
      fetchBlogPosts();
    } catch (error) {
      toast.error('Error deleting blog post');
    }
  };

  // Newsletter functions
  const saveNewsletter = async () => {
    if (!newsletterForm.subject || !newsletterForm.content) {
      toast.error('Please fill in subject and content');
      return;
    }

    try {
      if (editingNewsletter) {
        await axios.put(`${BACKEND_URL}/api/admin/newsletter/${editingNewsletter.id}`, newsletterForm);
        toast.success('Newsletter updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/newsletter`, newsletterForm);
        toast.success('Newsletter created');
      }
      
      setShowNewsletterEditor(false);
      setEditingNewsletter(null);
      setNewsletterForm({ subject: '', content: '', template: 'general', recipientType: 'all' });
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving newsletter');
    }
  };

  const editNewsletter = (newsletter) => {
    setNewsletterForm(newsletter);
    setEditingNewsletter(newsletter);
    setShowNewsletterEditor(true);
  };

  const sendNewsletter = async (newsletterId) => {
    if (!window.confirm('Are you sure you want to send this newsletter? This action cannot be undone.')) return;
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/admin/newsletter/${newsletterId}/send`);
      toast.success(response.data.message);
      fetchNewsletters();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error sending newsletter');
    }
  };

  const deleteNewsletter = async (newsletterId) => {
    if (!window.confirm('Are you sure you want to delete this newsletter?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/newsletter/${newsletterId}`);
      toast.success('Newsletter deleted');
      fetchNewsletters();
    } catch (error) {
      toast.error('Error deleting newsletter');
    }
  };

  // Application functions
  const updateApplicationStatus = async (type, id, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/applications/${type}/${id}/status?status=${newStatus}`);
      fetchApplications();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  // Contact functions
  const saveContact = async () => {
    if (!contactForm.name || !contactForm.email) {
      toast.error('Please fill in name and email');
      return;
    }

    try {
      if (editingContact) {
        await axios.put(`${BACKEND_URL}/api/admin/contacts/${editingContact.id}`, contactForm);
        toast.success('Contact updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/admin/contacts`, contactForm);
        toast.success('Contact created');
      }
      
      setShowAddContact(false);
      setEditingContact(null);
      setContactForm({ name: '', email: '', phone: '', contactType: 'subscriber', company: '', notes: '' });
      fetchContacts(contactFilter);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error saving contact');
    }
  };

  const deleteContact = async (contactId) => {
    if (!window.confirm('Are you sure you want to delete this contact?')) return;
    
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/contacts/${contactId}`);
      toast.success('Contact deleted');
      fetchContacts(contactFilter);
    } catch (error) {
      toast.error('Error deleting contact');
    }
  };

  const exportDonations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/donations/export`);
      const data = response.data;
      
      const csvContent = data.map(d => 
        `${d.firstName},${d.lastName},${d.email},${d.amount},${d.donationType},${d.companyName || ''},${d.status},${d.createdAt}`
      ).join('\n');
      
      const blob = new Blob([`First Name,Last Name,Email,Amount,Type,Company,Status,Date\n${csvContent}`], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `donations-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      toast.success('Donations exported!');
    } catch (error) {
      toast.error('Error exporting donations');
    }
  };

  const renderApplicationCard = (app, type) => {
    const isExpanded = expandedApp === `${type}-${app.id}`;
    
    return (
      <Card key={app.id} className="mb-4">
        <CardHeader 
          className="cursor-pointer hover:bg-gray-50"
          onClick={() => setExpandedApp(isExpanded ? null : `${type}-${app.id}`)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center ${type === 'cub' ? 'bg-cyan-100' : 'bg-amber-100'}`}>
                {type === 'cub' ? <GraduationCap className="w-6 h-6 text-cyan-600" /> : <Briefcase className="w-6 h-6 text-amber-600" />}
              </div>
              <div>
                <h3 className="font-semibold text-lg">{app.firstName} {app.lastName}</h3>
                <p className="text-sm text-gray-500">{app.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadge(app.status)}`}>{app.status}</span>
              <span className="text-sm text-gray-500">{formatDate(app.submittedAt)}</span>
              {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
            </div>
          </div>
        </CardHeader>
        
        {isExpanded && (
          <CardContent className="border-t">
            <div className="grid md:grid-cols-2 gap-6 pt-4">
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Contact Information</h4>
                <div className="flex items-center gap-2 text-sm"><Mail className="w-4 h-4 text-gray-400" /><span>{app.email}</span></div>
                <div className="flex items-center gap-2 text-sm"><Phone className="w-4 h-4 text-gray-400" /><span>{app.phone}</span></div>
                <div className="flex items-center gap-2 text-sm"><MapPin className="w-4 h-4 text-gray-400" /><span>{app.address}</span></div>
                <p className="text-sm"><strong>Date of Birth:</strong> {app.dateOfBirth}</p>
                <p className="text-sm"><strong>Gender:</strong> {app.gender}</p>
              </div>
              <div className="space-y-3">
                <h4 className="font-semibold text-gray-900 border-b pb-2">Background</h4>
                <p className="text-sm"><strong>Country of Birth:</strong> {app.countryOfBirth || 'N/A'}</p>
                <p className="text-sm"><strong>Country of Residence:</strong> {app.countryOfResidence || 'N/A'}</p>
                <p className="text-sm"><strong>Minority Status:</strong> {app.isMinority || 'N/A'}</p>
              </div>
              {type === 'cub' && (
                <>
                  <div className="space-y-3">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">Education</h4>
                    <p className="text-sm"><strong>Education Level:</strong> {app.ownEducation || 'N/A'}</p>
                    <p className="text-sm"><strong>GPA:</strong> {app.gpa || 'N/A'}</p>
                    <p className="text-sm"><strong>Occupation:</strong> {app.ownOccupation || 'N/A'}</p>
                  </div>
                  <div className="md:col-span-2 space-y-4">
                    <h4 className="font-semibold text-gray-900 border-b pb-2">Responses</h4>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-1">Why do you want to join?</p>
                      <p className="text-sm whitespace-pre-wrap">{app.whyJoin || 'N/A'}</p>
                    </div>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-gray-700 mb-1">Struggles overcome</p>
                      <p className="text-sm whitespace-pre-wrap">{app.struggles || 'N/A'}</p>
                    </div>
                  </div>
                </>
              )}
              {type === 'lion' && (
                <div className="md:col-span-2 space-y-4">
                  <h4 className="font-semibold text-gray-900 border-b pb-2">Mentorship Info</h4>
                  <p className="text-sm"><strong>Occupation:</strong> {app.occupation || 'N/A'}</p>
                  <div className="bg-gray-50 p-4 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-1">Why mentor?</p>
                    <p className="text-sm whitespace-pre-wrap">{app.whyMentor || 'N/A'}</p>
                  </div>
                </div>
              )}
              <div className="md:col-span-2 flex flex-wrap gap-2 pt-4 border-t">
                <Button 
                  size="sm" 
                  onClick={() => { setViewingApplication(app); setViewingApplicationType(type); }} 
                  className="bg-gradient-to-r from-cyan-500 to-blue-600 text-white"
                >
                  <Eye className="w-4 h-4 mr-1" /> View Full Application
                </Button>
                <div className="flex-1" />
                <Button size="sm" onClick={() => updateApplicationStatus(type, app.id, 'pending')} className="bg-yellow-500 hover:bg-yellow-600 text-white">Pending</Button>
                <Button size="sm" onClick={() => updateApplicationStatus(type, app.id, 'reviewed')} className="bg-blue-500 hover:bg-blue-600 text-white">Reviewed</Button>
                <Button size="sm" onClick={() => updateApplicationStatus(type, app.id, 'accepted')} className="bg-green-500 hover:bg-green-600 text-white">Accept</Button>
                <Button size="sm" onClick={() => updateApplicationStatus(type, app.id, 'rejected')} className="bg-red-500 hover:bg-red-600 text-white">Reject</Button>
              </div>
            </div>
          </CardContent>
        )}
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Admin Header */}
      <div className="bg-white border-b shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_pride-support/artifacts/0llcl2qk_lions%20logo.png" 
                alt="Lions Unleashed" 
                className="h-10 w-10"
              />
              <div>
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-500">Lions Unleashed Management</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-sm font-medium text-gray-900">{user?.name}</p>
                <p className="text-xs text-gray-500 capitalize">{user?.role?.replace('_', ' ')}</p>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => navigate('/')} title="View Website">
                  <Eye className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleLogout} className="text-red-600 hover:text-red-700 hover:bg-red-50">
                  <LogOut className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="flex flex-wrap gap-1 w-full max-w-5xl h-auto p-1">
            <TabsTrigger value="donations" className="flex items-center gap-2"><DollarSign className="w-4 h-4" />Donations</TabsTrigger>
            <TabsTrigger value="applications" className="flex items-center gap-2"><FileText className="w-4 h-4" />Applications</TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2"><Contact className="w-4 h-4" />Contacts</TabsTrigger>
            <TabsTrigger value="inbox" className="flex items-center gap-2 relative">
              <MessageCircle className="w-4 h-4" />Inbox
              {inboxStats.new > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs rounded-full flex items-center justify-center">
                  {inboxStats.new}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="blog" className="flex items-center gap-2"><PenSquare className="w-4 h-4" />Blog</TabsTrigger>
            <TabsTrigger value="newsletter" className="flex items-center gap-2"><Send className="w-4 h-4" />Newsletter</TabsTrigger>
            {hasPermission('users', 'view') && (
              <TabsTrigger value="users" className="flex items-center gap-2"><UserCog className="w-4 h-4" />Users</TabsTrigger>
            )}
          </TabsList>

          {/* Donations Tab */}
          <TabsContent value="donations">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Donations</CardTitle><Users className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{donationStats.totalDonations}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Amount</CardTitle><DollarSign className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">${donationStats.totalAmount.toLocaleString()}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Monthly</CardTitle><Calendar className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{donationStats.monthlyDonations}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">One-Time</CardTitle><TrendingUp className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{donationStats.oneTimeDonations}</div></CardContent></Card>
            </div>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle>Recent Donations</CardTitle>
                <Button variant="outline" onClick={exportDonations}>
                  <Download className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </CardHeader>
              <CardContent>
                {loading ? <p className="text-center py-8 text-gray-500">Loading...</p> : donations.length === 0 ? <p className="text-center py-8 text-gray-500">No donations yet.</p> : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead><tr className="border-b"><th className="text-left p-4 font-semibold">Donor</th><th className="text-left p-4 font-semibold">Email</th><th className="text-left p-4 font-semibold">Amount</th><th className="text-left p-4 font-semibold">Type</th><th className="text-left p-4 font-semibold">Status</th><th className="text-left p-4 font-semibold">Date</th></tr></thead>
                      <tbody>
                        {donations.map((d) => (
                          <tr key={d.id} className="border-b hover:bg-gray-50">
                            <td className="p-4">{d.anonymous ? <span className="italic text-gray-500">Anonymous</span> : `${d.firstName} ${d.lastName}`}</td>
                            <td className="p-4 text-sm text-gray-600">{d.email}</td>
                            <td className="p-4 font-semibold text-green-600">${d.amount.toFixed(2)}</td>
                            <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${d.donationType === 'monthly' ? 'bg-blue-100 text-blue-800' : 'bg-purple-100 text-purple-800'}`}>{d.donationType}</span></td>
                            <td className="p-4"><span className={`px-2 py-1 rounded text-xs ${getStatusBadge(d.status)}`}>{d.status}</span></td>
                            <td className="p-4 text-sm text-gray-600">{formatDate(d.createdAt)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Applications Tab */}
          <TabsContent value="applications">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Cub Applications</CardTitle><GraduationCap className="h-4 w-4 text-cyan-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{appStats.cubApplications.total}</div><p className="text-xs text-muted-foreground mt-1">{appStats.cubApplications.pending} pending</p></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Lion Applications</CardTitle><Briefcase className="h-4 w-4 text-amber-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{appStats.lionApplications.total}</div><p className="text-xs text-muted-foreground mt-1">{appStats.lionApplications.pending} pending</p></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Pending</CardTitle><FileText className="h-4 w-4 text-yellow-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{appStats.cubApplications.pending + appStats.lionApplications.pending}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Accepted</CardTitle><UserCheck className="h-4 w-4 text-green-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{appStats.cubApplications.accepted + appStats.lionApplications.accepted}</div></CardContent></Card>
            </div>
            <div className="mb-8">
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><GraduationCap className="w-6 h-6 text-cyan-600" />Cub Applications ({cubApplications.length})</h2>
              {cubApplications.length === 0 ? <Card><CardContent className="py-8 text-center text-gray-500">No Cub applications yet.</CardContent></Card> : cubApplications.map(app => renderApplicationCard(app, 'cub'))}
            </div>
            <div>
              <h2 className="text-2xl font-bold mb-4 flex items-center gap-2"><Briefcase className="w-6 h-6 text-amber-600" />Lion Applications ({lionApplications.length})</h2>
              {lionApplications.length === 0 ? <Card><CardContent className="py-8 text-center text-gray-500">No Lion applications yet.</CardContent></Card> : lionApplications.map(app => renderApplicationCard(app, 'lion'))}
            </div>
          </TabsContent>

          {/* Contacts Tab */}
          <TabsContent value="contacts">
            {/* Stats Cards */}
            <div className="grid md:grid-cols-6 gap-4 mb-8">
              <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => { setContactFilter('all'); fetchContacts('all'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Total Contacts</CardTitle>
                  <Users className="h-4 w-4 text-cyan-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contactStats.total}</div>
                  <p className="text-xs text-muted-foreground">{contactStats.unique_emails} unique emails</p>
                </CardContent>
              </Card>
              <Card className={`cursor-pointer hover:shadow-md transition-shadow ${contactFilter === 'subscribers' ? 'ring-2 ring-cyan-500' : ''}`} onClick={() => { setContactFilter('subscribers'); fetchContacts('subscribers'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Subscribers</CardTitle>
                  <Mail className="h-4 w-4 text-blue-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contactStats.by_type?.subscribers || 0}</div>
                </CardContent>
              </Card>
              <Card className={`cursor-pointer hover:shadow-md transition-shadow ${contactFilter === 'donors' ? 'ring-2 ring-cyan-500' : ''}`} onClick={() => { setContactFilter('donors'); fetchContacts('donors'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Donors</CardTitle>
                  <DollarSign className="h-4 w-4 text-green-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contactStats.by_type?.donors || 0}</div>
                </CardContent>
              </Card>
              <Card className={`cursor-pointer hover:shadow-md transition-shadow ${contactFilter === 'cubs' ? 'ring-2 ring-cyan-500' : ''}`} onClick={() => { setContactFilter('cubs'); fetchContacts('cubs'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Cubs</CardTitle>
                  <GraduationCap className="h-4 w-4 text-cyan-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contactStats.by_type?.cubs || 0}</div>
                </CardContent>
              </Card>
              <Card className={`cursor-pointer hover:shadow-md transition-shadow ${contactFilter === 'lions' ? 'ring-2 ring-cyan-500' : ''}`} onClick={() => { setContactFilter('lions'); fetchContacts('lions'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Lions</CardTitle>
                  <Briefcase className="h-4 w-4 text-amber-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{contactStats.by_type?.lions || 0}</div>
                </CardContent>
              </Card>
              <Card className={`cursor-pointer hover:shadow-md transition-shadow ${contactFilter === 'leaders' ? 'ring-2 ring-cyan-500' : ''}`} onClick={() => { setContactFilter('leaders'); fetchContacts('leaders'); }}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <CardTitle className="text-sm font-medium">Leaders</CardTitle>
                  <UserCheck className="h-4 w-4 text-purple-600" />
                </CardHeader>
                <CardContent>
                  <div className="text-2xl font-bold">{(contactStats.by_type?.leaders || 0) + (contactStats.by_type?.mentors || 0)}</div>
                </CardContent>
              </Card>
            </div>

            {/* Search, Filter, and Add Contact */}
            <div className="flex flex-wrap gap-4 mb-6">
              <div className="relative flex-1 min-w-[200px] max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                <Input 
                  placeholder="Search by name or email..." 
                  value={contactSearch}
                  onChange={(e) => setContactSearch(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Select value={contactFilter} onValueChange={(value) => { setContactFilter(value); fetchContacts(value); }}>
                <SelectTrigger className="w-48">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Contacts</SelectItem>
                  <SelectItem value="subscribers">Subscribers</SelectItem>
                  <SelectItem value="donors">Donors</SelectItem>
                  <SelectItem value="cubs">Cubs</SelectItem>
                  <SelectItem value="lions">Lions</SelectItem>
                  <SelectItem value="mentors">Mentors</SelectItem>
                  <SelectItem value="leaders">Leaders</SelectItem>
                </SelectContent>
              </Select>
              <Button variant="outline" onClick={() => {
                const csvContent = contacts
                  .filter(c => {
                    if (!contactSearch) return true;
                    const search = contactSearch.toLowerCase();
                    return c.name?.toLowerCase().includes(search) || c.email?.toLowerCase().includes(search);
                  })
                  .map(c => `${c.name},${c.email},${c.phone || ''},${c.type},${c.status}`)
                  .join('\n');
                const blob = new Blob([`Name,Email,Phone,Type,Status\n${csvContent}`], { type: 'text/csv' });
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `contacts-${contactFilter}-${new Date().toISOString().split('T')[0]}.csv`;
                a.click();
                toast.success('Contacts exported!');
              }}>
                <Download className="w-4 h-4 mr-2" />
                Export CSV
              </Button>
              <Button onClick={() => { setShowAddContact(true); setEditingContact(null); setContactForm({ name: '', email: '', phone: '', contactType: 'subscriber', company: '', notes: '' }); }} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                <Plus className="w-4 h-4 mr-2" />
                Add Contact
              </Button>
            </div>

            {/* Add/Edit Contact Form */}
            {showAddContact && (
              <Card className="mb-6">
                <CardHeader>
                  <CardTitle>{editingContact ? 'Edit Contact' : 'Add New Contact'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Name *</Label>
                      <Input 
                        value={contactForm.name} 
                        onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))} 
                        placeholder="Full name" 
                      />
                    </div>
                    <div>
                      <Label>Email *</Label>
                      <Input 
                        type="email"
                        value={contactForm.email} 
                        onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))} 
                        placeholder="email@example.com" 
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input 
                        value={contactForm.phone} 
                        onChange={(e) => setContactForm(prev => ({ ...prev, phone: e.target.value }))} 
                        placeholder="+1-555-0000" 
                      />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label>Contact Type *</Label>
                      <Select value={contactForm.contactType} onValueChange={(value) => setContactForm(prev => ({ ...prev, contactType: value }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="subscriber">Subscriber</SelectItem>
                          <SelectItem value="donor">Donor</SelectItem>
                          <SelectItem value="cub">Cub</SelectItem>
                          <SelectItem value="lion">Lion</SelectItem>
                          <SelectItem value="mentor">Mentor</SelectItem>
                          <SelectItem value="leader">Leader</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label>Company/Organization</Label>
                      <Input 
                        value={contactForm.company} 
                        onChange={(e) => setContactForm(prev => ({ ...prev, company: e.target.value }))} 
                        placeholder="Company name" 
                      />
                    </div>
                    <div>
                      <Label>Notes</Label>
                      <Input 
                        value={contactForm.notes} 
                        onChange={(e) => setContactForm(prev => ({ ...prev, notes: e.target.value }))} 
                        placeholder="Additional notes" 
                      />
                    </div>
                  </div>
                  <div className="flex gap-4 pt-2">
                    <Button onClick={saveContact} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                      {editingContact ? 'Update Contact' : 'Save Contact'}
                    </Button>
                    <Button variant="outline" onClick={() => { setShowAddContact(false); setEditingContact(null); }}>
                      Cancel
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Contacts Table */}
            <Card>
              <CardHeader>
                <CardTitle>Contact List ({contacts.filter(c => {
                  if (!contactSearch) return true;
                  const search = contactSearch.toLowerCase();
                  return c.name?.toLowerCase().includes(search) || c.email?.toLowerCase().includes(search);
                }).length})</CardTitle>
              </CardHeader>
              <CardContent>
                {loading ? (
                  <p className="text-center py-8 text-gray-500">Loading...</p>
                ) : contacts.length === 0 ? (
                  <p className="text-center py-8 text-gray-500">No contacts found.</p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left p-4 font-semibold">Name</th>
                          <th className="text-left p-4 font-semibold">Email</th>
                          <th className="text-left p-4 font-semibold">Phone</th>
                          <th className="text-left p-4 font-semibold">Type</th>
                          <th className="text-left p-4 font-semibold">Source</th>
                          <th className="text-left p-4 font-semibold">Status</th>
                          <th className="text-left p-4 font-semibold">Date</th>
                          <th className="text-left p-4 font-semibold">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {contacts
                          .filter(c => {
                            if (!contactSearch) return true;
                            const search = contactSearch.toLowerCase();
                            return c.name?.toLowerCase().includes(search) || c.email?.toLowerCase().includes(search);
                          })
                          .map((contact, index) => (
                          <tr key={`${contact.id}-${index}`} className="border-b hover:bg-gray-50">
                            <td className="p-4 font-medium">{contact.name || '-'}</td>
                            <td className="p-4">
                              <a href={`mailto:${contact.email}`} className="text-cyan-600 hover:underline">
                                {contact.email}
                              </a>
                            </td>
                            <td className="p-4 text-sm text-gray-600">{contact.phone || '-'}</td>
                            <td className="p-4">
                              <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                                contact.type === 'Subscriber' ? 'bg-blue-100 text-blue-800' :
                                contact.type === 'Donor' ? 'bg-green-100 text-green-800' :
                                contact.type === 'Cub' ? 'bg-cyan-100 text-cyan-800' :
                                contact.type === 'Lion' ? 'bg-amber-100 text-amber-800' :
                                contact.type === 'Mentor' ? 'bg-purple-100 text-purple-800' :
                                contact.type === 'Leader' ? 'bg-pink-100 text-pink-800' :
                                'bg-gray-100 text-gray-800'
                              }`}>
                                {contact.type}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-gray-600">{contact.source}</td>
                            <td className="p-4">
                              <span className={`inline-block px-2 py-1 rounded text-xs ${getStatusBadge(contact.status)}`}>
                                {contact.status}
                              </span>
                            </td>
                            <td className="p-4 text-sm text-gray-600">
                              {contact.date ? new Date(contact.date).toLocaleDateString() : '-'}
                            </td>
                            <td className="p-4">
                              {contact.isManual ? (
                                <div className="flex gap-1">
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => { 
                                      setContactForm({
                                        name: contact.name,
                                        email: contact.email,
                                        phone: contact.phone || '',
                                        contactType: contact.type.toLowerCase(),
                                        company: contact.details?.company || '',
                                        notes: contact.details?.notes || ''
                                      });
                                      setEditingContact(contact);
                                      setShowAddContact(true);
                                    }}
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button 
                                    size="sm" 
                                    variant="outline" 
                                    onClick={() => deleteContact(contact.id)}
                                    className="text-red-500 hover:text-red-700"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">-</span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Blog Tab */}
          <TabsContent value="blog">
            {showBlogEditor ? (
              <BlogEditor
                post={editingPost}
                onSave={async (postData) => {
                  try {
                    if (editingPost) {
                      await axios.put(`${BACKEND_URL}/api/admin/blog/${editingPost.id}`, postData);
                      toast.success('Blog post updated');
                    } else {
                      await axios.post(`${BACKEND_URL}/api/admin/blog`, postData);
                      toast.success('Blog post created');
                    }
                    setShowBlogEditor(false);
                    setEditingPost(null);
                    fetchBlogPosts();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error saving blog post');
                  }
                }}
                onCancel={() => { setShowBlogEditor(false); setEditingPost(null); }}
              />
            ) : (
              <>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Blog Posts ({blogPosts.length})</h2>
                  <Button onClick={() => { setShowBlogEditor(true); setEditingPost(null); }} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                    <Plus className="w-4 h-4 mr-2" /> New Post
                  </Button>
                </div>
                <div className="space-y-4">
                  {blogPosts.length === 0 ? (
                    <Card><CardContent className="py-12 text-center text-gray-500">No blog posts yet. Create your first post!</CardContent></Card>
                  ) : (
                    blogPosts.map(post => (
                      <Card key={post.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-start justify-between">
                            <div className="flex gap-4">
                              {post.featureImage && (
                                <img src={post.featureImage} alt="" className="w-24 h-24 object-cover rounded-lg" />
                              )}
                              <div>
                                <h3 className="text-xl font-semibold">{post.title}</h3>
                                <p className="text-sm text-gray-500 mt-1">By {post.author} • {formatDate(post.createdAt)}</p>
                                {post.excerpt && <p className="text-gray-600 mt-2 line-clamp-2">{post.excerpt}</p>}
                                <div className="flex items-center gap-2 mt-3">
                                  <span className={`px-2 py-1 rounded text-xs ${post.status === 'scheduled' ? 'bg-amber-100 text-amber-800' : getStatusBadge(post.status)}`}>
                                    {post.status}
                                    {post.status === 'scheduled' && post.scheduledAt && (
                                      <span className="ml-1">• {new Date(post.scheduledAt).toLocaleDateString()} {new Date(post.scheduledAt).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}</span>
                                    )}
                                  </span>
                                  {post.tags?.map(tag => <span key={tag} className="px-2 py-1 bg-gray-100 rounded text-xs">{tag}</span>)}
                                </div>
                              </div>
                            </div>
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => { setEditingPost(post); setShowBlogEditor(true); }}><Edit className="w-4 h-4" /></Button>
                              <Button size="sm" variant="outline" onClick={() => deleteBlogPost(post.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Newsletter Tab */}
          <TabsContent value="newsletter">
            {showNewsletterEditor ? (
              <NewsletterBuilder
                newsletter={editingNewsletter}
                subscriberStats={subscriberStats}
                onSave={async (newsletterData) => {
                  try {
                    let newsletterId;
                    if (editingNewsletter) {
                      await axios.put(`${BACKEND_URL}/api/admin/newsletter/${editingNewsletter.id}`, newsletterData);
                      newsletterId = editingNewsletter.id;
                      toast.success('Newsletter updated');
                    } else {
                      const response = await axios.post(`${BACKEND_URL}/api/admin/newsletter`, newsletterData);
                      newsletterId = response.data.id;
                      toast.success('Newsletter created');
                    }
                    
                    // If sendNow flag is set, send immediately
                    if (newsletterData.sendNow && newsletterId) {
                      try {
                        const sendResponse = await axios.post(`${BACKEND_URL}/api/admin/newsletter/${newsletterId}/send`);
                        toast.success(sendResponse.data.message);
                      } catch (sendError) {
                        toast.error('Newsletter saved but failed to send');
                      }
                    }
                    
                    setShowNewsletterEditor(false);
                    setEditingNewsletter(null);
                    fetchNewsletters();
                  } catch (error) {
                    toast.error(error.response?.data?.detail || 'Error saving newsletter');
                  }
                }}
                onCancel={() => { setShowNewsletterEditor(false); setEditingNewsletter(null); }}
              />
            ) : (
              <>
                <div className="grid md:grid-cols-4 gap-6 mb-8">
                  <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Subscribers</CardTitle><Users className="h-4 w-4 text-cyan-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{subscriberStats.active}</div><p className="text-xs text-muted-foreground mt-1">Active subscribers</p></CardContent></Card>
                  <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Donors</CardTitle><DollarSign className="h-4 w-4 text-green-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{subscriberStats.totalReach?.donors || 0}</div><p className="text-xs text-muted-foreground mt-1">Completed donations</p></CardContent></Card>
                  <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Applicants</CardTitle><FileText className="h-4 w-4 text-amber-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{subscriberStats.totalReach?.applicants || 0}</div><p className="text-xs text-muted-foreground mt-1">All applications</p></CardContent></Card>
                  <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Reach</CardTitle><Send className="h-4 w-4 text-blue-600" /></CardHeader><CardContent><div className="text-2xl font-bold">{(subscriberStats.active || 0) + (subscriberStats.totalReach?.donors || 0) + (subscriberStats.totalReach?.applicants || 0)}</div><p className="text-xs text-muted-foreground mt-1">Unique recipients</p></CardContent></Card>
                </div>

                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold">Newsletters ({newsletters.length})</h2>
                  <Button onClick={() => { setShowNewsletterEditor(true); setEditingNewsletter(null); }} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                    <Plus className="w-4 h-4 mr-2" /> Create Newsletter
                  </Button>
                </div>

                <div className="space-y-4">
                  {newsletters.length === 0 ? (
                    <Card><CardContent className="py-12 text-center text-gray-500">No newsletters yet. Create your first newsletter!</CardContent></Card>
                  ) : (
                    newsletters.map(newsletter => (
                      <Card key={newsletter.id} className="hover:shadow-md transition-shadow">
                        <CardContent className="p-6">
                          <div className="flex items-center justify-between">
                            <div>
                              <h3 className="text-xl font-semibold">{newsletter.subject}</h3>
                              <p className="text-sm text-gray-500 mt-1">
                                {newsletter.template} • {newsletter.recipientType} recipients
                                {newsletter.status === 'sent' && ` • Sent to ${newsletter.recipientCount} on ${formatDate(newsletter.sentAt)}`}
                              </p>
                              <span className={`inline-block mt-2 px-2 py-1 rounded text-xs ${getStatusBadge(newsletter.status)}`}>{newsletter.status}</span>
                            </div>
                            <div className="flex gap-2">
                              {newsletter.status !== 'sent' && (
                                <>
                                  <Button size="sm" onClick={() => sendNewsletter(newsletter.id)} className="bg-green-500 hover:bg-green-600 text-white"><Send className="w-4 h-4 mr-1" /> Send</Button>
                                  <Button size="sm" variant="outline" onClick={() => { setEditingNewsletter(newsletter); setShowNewsletterEditor(true); }}><Edit className="w-4 h-4" /></Button>
                                </>
                              )}
                              <Button size="sm" variant="outline" onClick={() => deleteNewsletter(newsletter.id)} className="text-red-500 hover:text-red-700"><Trash2 className="w-4 h-4" /></Button>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    ))
                  )}
                </div>
              </>
            )}
          </TabsContent>

          {/* Inbox Tab - Contact Form Submissions */}
          <TabsContent value="inbox">
            <div className="grid md:grid-cols-4 gap-6 mb-8">
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Total Messages</CardTitle><MessageCircle className="h-4 w-4 text-muted-foreground" /></CardHeader><CardContent><div className="text-2xl font-bold">{inboxStats.total}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">New</CardTitle><Circle className="h-4 w-4 text-blue-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-blue-600">{inboxStats.new}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Read</CardTitle><Eye className="h-4 w-4 text-yellow-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-yellow-600">{inboxStats.read}</div></CardContent></Card>
              <Card><CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2"><CardTitle className="text-sm font-medium">Replied</CardTitle><CheckCircle className="h-4 w-4 text-green-500" /></CardHeader><CardContent><div className="text-2xl font-bold text-green-600">{inboxStats.replied}</div></CardContent></Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Contact Form Submissions
                </CardTitle>
              </CardHeader>
              <CardContent>
                {contactSubmissions.length === 0 ? (
                  <p className="text-center text-gray-500 py-8">No contact form submissions yet</p>
                ) : (
                  <div className="space-y-4">
                    {contactSubmissions.map((submission) => (
                      <div 
                        key={submission.id} 
                        className={`p-4 rounded-lg border ${
                          submission.status === 'new' ? 'bg-blue-50 border-blue-200' :
                          submission.status === 'read' ? 'bg-yellow-50 border-yellow-200' :
                          'bg-green-50 border-green-200'
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                submission.status === 'new' ? 'bg-blue-100 text-blue-800' :
                                submission.status === 'read' ? 'bg-yellow-100 text-yellow-800' :
                                'bg-green-100 text-green-800'
                              }`}>
                                {submission.status}
                              </span>
                              <span className="text-sm text-gray-500">{formatDate(submission.createdAt)}</span>
                            </div>
                            <h3 className="font-semibold text-gray-900">{submission.subject}</h3>
                            <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                              <span className="flex items-center gap-1"><Users className="w-4 h-4" />{submission.name}</span>
                              <span className="flex items-center gap-1"><Mail className="w-4 h-4" />{submission.email}</span>
                            </div>
                            <p className="text-gray-700 mt-3 whitespace-pre-wrap">{submission.message}</p>
                            
                            {submission.status === 'replied' && submission.replyMessage && (
                              <div className="mt-4 p-3 bg-white rounded-lg border border-gray-200">
                                <p className="text-xs text-gray-500 mb-1">
                                  Reply sent by {submission.repliedBy} on {formatDate(submission.repliedAt)}
                                </p>
                                <p className="text-sm text-gray-700 whitespace-pre-wrap">{submission.replyMessage}</p>
                              </div>
                            )}
                          </div>
                          <div className="flex items-center gap-2 ml-4">
                            {submission.status !== 'replied' && (
                              <Button 
                                size="sm" 
                                onClick={() => handleOpenReply(submission)}
                                className="bg-cyan-600 hover:bg-cyan-700"
                              >
                                <Reply className="w-4 h-4 mr-1" /> Reply
                              </Button>
                            )}
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeleteContact(submission.id)}
                              className="text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Reply Modal */}
            <Dialog open={showReplyModal} onOpenChange={setShowReplyModal}>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Reply to {selectedContact?.name}</DialogTitle>
                </DialogHeader>
                {selectedContact && (
                  <div className="space-y-4 py-4">
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Original Message:</p>
                      <p className="font-medium">{selectedContact.subject}</p>
                      <p className="text-gray-700 mt-2 whitespace-pre-wrap">{selectedContact.message}</p>
                    </div>
                    
                    <div>
                      <Label>Your Reply</Label>
                      <Textarea
                        value={replyMessage}
                        onChange={(e) => setReplyMessage(e.target.value)}
                        placeholder="Type your reply here..."
                        className="mt-2 min-h-[150px]"
                      />
                    </div>

                    <div className="flex justify-end gap-2">
                      <Button variant="outline" onClick={() => setShowReplyModal(false)}>Cancel</Button>
                      <Button onClick={handleSendReply} className="bg-cyan-600 hover:bg-cyan-700">
                        <Send className="w-4 h-4 mr-2" /> Send Reply
                      </Button>
                    </div>
                  </div>
                )}
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* Users Tab */}
          {hasPermission('users', 'view') && (
            <TabsContent value="users">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Shield className="w-5 h-5" />
                    Admin Users
                  </CardTitle>
                  {hasPermission('users', 'create') && (
                    <Button onClick={openNewUser} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                      <Plus className="w-4 h-4 mr-2" /> Add User
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {adminUsers.map((adminUser) => (
                      <div key={adminUser.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border">
                        <div className="flex items-center gap-4">
                          <div className={`w-12 h-12 rounded-full flex items-center justify-center text-white font-bold ${
                            adminUser.role === 'super_admin' ? 'bg-purple-600' :
                            adminUser.role === 'admin' ? 'bg-blue-600' : 'bg-gray-600'
                          }`}>
                            {adminUser.name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <h3 className="font-semibold text-gray-900">{adminUser.name}</h3>
                            <p className="text-sm text-gray-500">{adminUser.email}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                                adminUser.role === 'super_admin' ? 'bg-purple-100 text-purple-800' :
                                adminUser.role === 'admin' ? 'bg-blue-100 text-blue-800' : 'bg-gray-100 text-gray-800'
                              }`}>
                                {adminUser.role?.replace('_', ' ')}
                              </span>
                              {!adminUser.isActive && (
                                <span className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-full">Inactive</span>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {adminUser.lastLoginAt && (
                            <span className="text-xs text-gray-400">
                              Last login: {new Date(adminUser.lastLoginAt).toLocaleDateString()}
                            </span>
                          )}
                          {hasPermission('users', 'edit') && adminUser.id !== user?.id && (
                            <>
                              <Button variant="outline" size="sm" onClick={() => handleResetPassword(adminUser.id)} title="Reset Password">
                                <Key className="w-4 h-4" />
                              </Button>
                              <Button variant="outline" size="sm" onClick={() => openEditUser(adminUser)}>
                                <Edit className="w-4 h-4" />
                              </Button>
                            </>
                          )}
                          {hasPermission('users', 'delete') && adminUser.id !== user?.id && (
                            <Button variant="outline" size="sm" onClick={() => handleDeleteUser(adminUser.id)} className="text-red-600 hover:bg-red-50">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                    {adminUsers.length === 0 && (
                      <p className="text-center text-gray-500 py-8">No admin users found</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* User Form Modal */}
              <Dialog open={showUserForm} onOpenChange={setShowUserForm}>
                <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-6 py-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Name *</Label>
                        <Input
                          value={userForm.name}
                          onChange={(e) => setUserForm(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Full name"
                        />
                      </div>
                      <div>
                        <Label>Email *</Label>
                        <Input
                          type="email"
                          value={userForm.email}
                          onChange={(e) => setUserForm(prev => ({ ...prev, email: e.target.value }))}
                          placeholder="email@example.com"
                          disabled={!!editingUser}
                        />
                      </div>
                    </div>

                    {!editingUser && (
                      <div>
                        <Label>Password *</Label>
                        <Input
                          type="password"
                          value={userForm.password}
                          onChange={(e) => setUserForm(prev => ({ ...prev, password: e.target.value }))}
                          placeholder="Enter password"
                        />
                      </div>
                    )}

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Role</Label>
                        <Select value={userForm.role} onValueChange={handleRoleChange}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="manager">Manager</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                            {user?.role === 'super_admin' && (
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                            )}
                          </SelectContent>
                        </Select>
                      </div>
                      {editingUser && (
                        <div className="flex items-center gap-2 pt-6">
                          <Checkbox
                            id="isActive"
                            checked={userForm.isActive}
                            onCheckedChange={(checked) => setUserForm(prev => ({ ...prev, isActive: checked }))}
                          />
                          <Label htmlFor="isActive">Active Account</Label>
                        </div>
                      )}
                    </div>

                    {/* Permissions Section */}
                    <div className="border-t pt-4">
                      <Label className="text-lg font-semibold">Permissions</Label>
                      <p className="text-sm text-gray-500 mb-4">Customize what this user can do</p>
                      
                      <div className="grid grid-cols-2 gap-4">
                        {permissionsSchema && Object.entries(permissionsSchema.permissions).map(([module, actions]) => (
                          <div key={module} className="p-3 bg-gray-50 rounded-lg">
                            <h4 className="font-medium capitalize mb-2">{module}</h4>
                            <div className="space-y-2">
                              {Object.entries(actions).map(([action, label]) => (
                                <div key={action} className="flex items-center gap-2">
                                  <Checkbox
                                    id={`${module}-${action}`}
                                    checked={(userForm.permissions[module] || []).includes(action)}
                                    onCheckedChange={() => togglePermission(module, action)}
                                  />
                                  <Label htmlFor={`${module}-${action}`} className="text-sm font-normal">{label}</Label>
                                </div>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t">
                      <Button variant="outline" onClick={() => setShowUserForm(false)}>Cancel</Button>
                      <Button onClick={handleSaveUser} className="bg-gradient-to-r from-cyan-500 to-blue-600">
                        {editingUser ? 'Update User' : 'Create User'}
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </TabsContent>
          )}
        </Tabs>

        <div className="mt-6 flex justify-end">
          <Button onClick={() => { fetchDonations(); fetchDonationStats(); fetchApplications(); fetchBlogPosts(); fetchNewsletters(); fetchContacts(); fetchAdminUsers(); fetchContactSubmissions(); }} className="bg-gradient-to-r from-cyan-500 to-blue-600">Refresh Data</Button>
        </div>
      </div>

      {/* Application Viewer Modal */}
      {viewingApplication && (
        <ApplicationViewer
          application={viewingApplication}
          type={viewingApplicationType}
          onClose={() => { setViewingApplication(null); setViewingApplicationType(null); }}
          onStatusChange={async (newStatus) => {
            await updateApplicationStatus(viewingApplicationType, viewingApplication.id, newStatus);
            setViewingApplication({ ...viewingApplication, status: newStatus });
          }}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
